import React, { useCallback, useContext } from 'react';
import AppContext from 'app/AppContext';
import {
    getProfileText,
    getProfileTooltip,
    StrikeBenchmark,
    toDisplayOption,
} from 'widgets/estimatedTradeCost/components/options';
import { strikeTimeToString } from 'widgets/estimatedTradeCost/store/data/mapping';
import { DetailsPanel } from 'utils/components/DetailsPanel/DetailsPanel';
import DetailsPanelValue from 'utils/components/DetailsPanel/DetailsPanelValue';
import { EstimatedTradeCostWidgetRequestQty } from 'widgets/estimatedTradeCost/widget/components/EstimatedTradeCostWidgetRequestQty';
import {
    maxPartLitHelp,
    minPartLitHelp,
} from 'widgets/estimatedTradeCost/widget/components/help/property-help';
import { usePortfolioSelectedSymbol } from 'portfolio/store/hooks';
import { useEstimatedTradeCostWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget/store/configslice';
import { EstimatedTradeCostOrderDataState } from 'widgets/estimatedTradeCost/store/data/EstimateTradeCostDataState';

export function EstimatedTradeCostWidgetRequestPanel({
    editedQty,
    data,
    onQtyChanged: onQtyChangedProps,
}: {
    editedQty: number | undefined;
    data: EstimatedTradeCostOrderDataState | undefined;
    onQtyChanged: (q: number) => void;
}) {
    const symbol = usePortfolioSelectedSymbol();
    const { config } = useEstimatedTradeCostWidgetConfigSlice();
    const {
        benchmark,
        strikeTime: configuredStrikeTime,
        profile,
        minPartLit,
        maxPartLit,
    } = config;
    const { numberFormat, timeFormat } = useContext(AppContext);

    const strikeTime =
        benchmark === StrikeBenchmark && configuredStrikeTime
            ? strikeTimeToString(configuredStrikeTime)
            : undefined;

    const onQtyChanged = useCallback(
        (qty: number) => {
            onQtyChangedProps(qty);
        },
        [onQtyChangedProps]
    );

    return (
        <DetailsPanel className="RequestPanel">
            <DetailsPanelValue label="Symbol" value={symbol} />
            <EstimatedTradeCostWidgetRequestQty
                editedQty={editedQty}
                request={data?.request}
                onQtyChanged={onQtyChanged}
            />
            <DetailsPanelValue
                label="Benchmark"
                value={toDisplayOption(benchmark)}
            />
            <DetailsPanelValue
                label="Profile"
                value={getProfileText(profile)}
                valueTooltip={getProfileTooltip(profile)}
            />
            <DetailsPanelValue
                label="Min Part Lit"
                value={numberFormat.n0(minPartLit) + '%'}
                tooltip={minPartLitHelp}
            />
            <DetailsPanelValue
                label="Max Part Lit"
                value={numberFormat.n0(maxPartLit) + '%'}
                tooltip={maxPartLitHelp}
            />
            {strikeTime && (
                <DetailsPanelValue label="Strike Time" value={strikeTime} />
            )}
            {!editedQty && data && (
                <DetailsPanelValue
                    label="Request Time"
                    value={timeFormat.toTimeMinutes(data.requestTimestamp)}
                    tooltip={'Time that the request was submitted'}
                />
            )}
        </DetailsPanel>
    );
}
